import { useCallback, useEffect, useState } from 'react';
import { errorMessage, type RegistryApi } from '../../api/registryApi';
import type {
  RegistryApproval,
  RegistryApprovalStatus,
  RegistryDecision,
} from '../../api/registryApiTypes';
import { useReloadWhenStale } from './useReloadWhenStale';

export interface RegistryApprovalsState {
  approvals: RegistryApproval[];
  loading: boolean;
  /** The last load or action failure, as the server worded it. */
  error: string | null;
  clearError: () => void;
  reload: () => void;
  decide: (approvalId: string, decision: RegistryDecision, comment?: string) => Promise<boolean>;
  raiseTest: (title: string, amount?: string) => Promise<boolean>;
}

export function useRegistryApprovals(
  api: RegistryApi,
  status: RegistryApprovalStatus,
): RegistryApprovalsState {
  const [approvals, setApprovals] = useState<RegistryApproval[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .listApprovals(status)
      .then((result) => {
        if (cancelled) return;
        setApprovals(result);
        setLoading(false);
      })
      .catch((loadError) => {
        if (cancelled) return;
        setApprovals([]);
        setError(errorMessage(loadError));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, status, version]);

  const reload = useCallback(() => setVersion((n) => n + 1), []);

  useReloadWhenStale(reload);

  const run = useCallback(
    async (action: () => Promise<unknown>) => {
      setError(null);
      try {
        await action();
        reload();
        return true;
      } catch (actionError) {
        setError(errorMessage(actionError));
        return false;
      }
    },
    [reload],
  );

  const decide = useCallback(
    (approvalId: string, decision: RegistryDecision, comment?: string) =>
      run(() => api.decideApproval(approvalId, decision, comment)),
    [api, run],
  );

  const raiseTest = useCallback(
    (title: string, amount?: string) => run(() => api.createTestApproval(title, amount)),
    [api, run],
  );

  return {
    approvals,
    loading,
    error,
    clearError: () => setError(null),
    reload,
    decide,
    raiseTest,
  };
}
